// Counts for All events, Overview, the sidebar and the People filters.
import { windowLeft, windowOpen } from "./cap";
import type { Person } from "./people";
import { GOT, isRed, ON_THE_WAY, type PStatus } from "./status";

/** "Replies waiting" = conversations with unread > 0 (drops on open, not on send). */
export const isWaiting = (p: Person) => (p.conv?.unread ?? 0) > 0;

/** Wrote to us at least once. Every messaged phone has a conversation doc (mark_sent), so this is the "Replied" test. */
export const hasReplied = (p: Person) => p.conv?.lastInboundAt != null;

export const needsAttention = (p: Person) => isWaiting(p) || isRed(p.status) || p.status === "over_limit";

/** Overview order: waiting replies by soonest window close, then failed, then invalid, then over the limit. */
export function attentionRank(p: Person, now: number): number {
  if (isWaiting(p)) return Math.max(windowLeft(p.conv, now), 0) / (48 * 3600e3);
  return p.status === "failed" ? 1 : p.status === "invalid" ? 2 : 3;
}

export interface OverviewCounts {
  /** people on the list (sheet or ledger); `not_on_list` excluded */
  people: number;
  notOnList: number;
  /** sheet rows in the snapshot (Reg.inSheet), minus ledger `duplicate`s: the Google Sheet row count */
  registrations: number;
  /** the same registrations, each by its own Reg.status (opted-out phone wins), for the Registrations card */
  regs: RegCounts;
  phones: number;
  byStatus: Record<PStatus, number>;
  /** a ledger row exists that is not `invalid` (some send was attempted) */
  messaged: number;
  got: number;
  read: number;
  /** (delivered + read + opted_out) / people, 0–100 */
  coveragePct: number;
  waiting: number;
  fix: number;
  attention: number;
  /** people who wrote to us */
  conversations: number;
}

export interface RegCounts {
  byStatus: Record<PStatus, number>;
  /** a ledger row exists that is not `invalid` */
  messaged: number;
  got: number;
  read: number;
}

export const zero = (): Record<PStatus, number> => ({
  queued: 0,
  sending: 0,
  sent: 0,
  delivered: 0,
  read: 0,
  retrying: 0,
  over_limit: 0,
  failed: 0,
  invalid: 0,
  opted_out: 0,
  not_on_list: 0,
});

const gotOf = (by: Record<PStatus, number>) => GOT.reduce((n, s) => n + by[s], 0);

const wasAttempted = (p: Person) => p.regs.some((r) => !r.notRetried && r.ledger && r.ledger.status !== "invalid");

export function overviewCounts(people: Person[]): OverviewCounts {
  const byStatus = zero();
  const regBy = zero();
  let regMessaged = 0;
  let registrations = 0;
  let messaged = 0;
  let waiting = 0;
  let attention = 0;
  let conversations = 0;
  for (const p of people) {
    byStatus[p.status]++;
    for (const r of p.regs) {
      // inSheet rows are never notRetried; `null` (duplicate) never sends and has no segment.
      if (!r.inSheet || r.status == null) continue;
      registrations++;
      regBy[p.optedOut ? "opted_out" : r.status]++;
      if (r.ledger && r.ledger.status !== "invalid") regMessaged++;
    }
    if (wasAttempted(p)) messaged++;
    if (isWaiting(p)) waiting++;
    if (needsAttention(p)) attention++;
    if (hasReplied(p)) conversations++;
  }
  const got = gotOf(byStatus);
  const onList = people.length - byStatus.not_on_list;
  return {
    people: onList,
    notOnList: byStatus.not_on_list,
    registrations,
    regs: { byStatus: regBy, messaged: regMessaged, got: gotOf(regBy), read: regBy.read },
    phones: people.filter((p) => p.phone && p.status !== "not_on_list").length,
    byStatus,
    messaged,
    got,
    read: byStatus.read,
    coveragePct: onList ? Math.round((got / onList) * 100) : 0,
    waiting,
    fix: byStatus.failed + byStatus.invalid,
    attention,
    conversations,
  };
}

/** Bar + legend segments on Overview (prototype SEGS). `key` is the People `?s=` filter. */
export const SEGMENTS: { key: string; label: string; color: string; match: (s: PStatus) => boolean }[] = [
  { key: "read", label: "Read", color: "var(--st-read)", match: (s) => s === "read" },
  { key: "unread", label: "Delivered, not read", color: "var(--st-deliv)", match: (s) => s === "delivered" },
  { key: "onway", label: "On the way", color: "var(--st-sent)", match: (s) => ON_THE_WAY.includes(s) },
  { key: "retrying", label: "Retrying", color: "var(--st-retry)", match: (s) => s === "retrying" },
  { key: "over_limit", label: "Over the limit", color: "var(--warning)", match: (s) => s === "over_limit" },
  { key: "fix", label: "Failed or invalid number", color: "var(--st-bad)", match: isRed },
  { key: "opted_out", label: "Opted out", color: "var(--tertiary)", match: (s) => s === "opted_out" },
  { key: "queued", label: "Not sent yet", color: "var(--st-wait)", match: (s) => s === "queued" },
];

/** Bar + legend counts from a status tally (per person or per registration). Segments partition the tally. */
export function segmentCounts(byStatus: Record<PStatus, number>) {
  return SEGMENTS.map((s) => ({
    ...s,
    n: (Object.keys(byStatus) as PStatus[]).filter(s.match).reduce((t, k) => t + byStatus[k], 0),
  }));
}

// ---------------------------------------------------------------- People filters

export type View = "all" | "attention" | "replied";
export const VIEW_CHIPS: Record<View, string[]> = {
  all: [
    "any",
    "queued",
    "sent",
    "delivered",
    "read",
    "retrying",
    "over_limit",
    "failed",
    "invalid",
    "opted_out",
    "not_on_list",
    "family",
  ],
  attention: ["any", "waiting", "failed", "invalid", "over_limit"],
  replied: ["any", "waiting", "open", "closed"],
};

/** Chip / `?s=` predicate. Unknown keys match nobody. */
export function chipFilter(k: string, now: number): (p: Person) => boolean {
  switch (k) {
    case "any":
      return () => true;
    case "waiting":
      return isWaiting;
    case "open":
      return (p) => windowOpen(p.conv, p.optedOut, now);
    case "closed":
      return (p) => hasReplied(p) && !windowOpen(p.conv, p.optedOut, now);
    case "onway":
      return (p) => ON_THE_WAY.includes(p.status);
    case "got":
      return (p) => GOT.includes(p.status);
    case "fix":
      return (p) => isRed(p.status);
    case "messaged":
      return wasAttempted;
    case "family":
      return (p) => p.regs.length > 1;
    // Delivery chips are a funnel: everyone delivered was also sent, everyone read was also delivered.
    case "sent":
      return (p) => p.status === "sent" || p.status === "delivered" || p.status === "read";
    case "delivered":
      return (p) => p.status === "delivered" || p.status === "read";
    // Exclusive "delivered, not read" for the Overview bar segment (the bar is a partition).
    case "unread":
      return (p) => p.status === "delivered";
    case "queued":
    case "sending":
    case "read":
    case "retrying":
    case "over_limit":
    case "failed":
    case "invalid":
    case "opted_out":
    case "not_on_list":
      return (p) => p.status === k;
    default:
      return () => false;
  }
}

const ALL_ORDER: Partial<Record<PStatus, number>> = { failed: 0, invalid: 1, over_limit: 2, retrying: 3, sending: 4 };

/** Base list per view, already sorted (prototype baseList). */
export function viewList(people: Person[], view: View, now: number): Person[] {
  if (view === "attention") {
    return people.filter(needsAttention).sort((a, b) => attentionRank(a, now) - attentionRank(b, now));
  }
  if (view === "replied") {
    return people
      .filter(hasReplied)
      .sort((a, b) => Number(isWaiting(b)) - Number(isWaiting(a)) || (b.lastAt ?? 0) - (a.lastAt ?? 0));
  }
  return [...people].sort(
    (a, b) =>
      (ALL_ORDER[a.status] ?? 9) - (ALL_ORDER[b.status] ?? 9) ||
      Number(isWaiting(b)) - Number(isWaiting(a)) ||
      (b.lastAt ?? 0) - (a.lastAt ?? 0) ||
      a.name.localeCompare(b.name),
  );
}

/** Search: name or reg id (any registration on the phone), or phone digits (3+). */
export function matchesSearch(p: Person, q: string): boolean {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  if (p.regs.some((r) => r.name.toLowerCase().includes(s) || r.key.toLowerCase().includes(s))) return true;
  if (p.name.toLowerCase().includes(s)) return true;
  const digits = s.replace(/\D/g, "");
  return digits.length >= 3 && digits === s.replace(/[\s+]/g, "") && !!p.phone?.includes(digits);
}
